import { useCallback, useSyncExternalStore } from 'react'

/**
 * Subscribes to a media query.
 *
 * For layout that is real STATE rather than styling. The drawer needs a focus
 * trap, the rail needs tooltips, and a table has to know which columns are in the
 * DOM so a totals row can span the right number of them — none of that can be
 * expressed in CSS, which only hides things after the fact.
 *
 * Reach for a Tailwind `sm:`/`md:` class first. Use this only when JavaScript has
 * to know the answer.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query)
      list.addEventListener('change', onChange)
      return () => list.removeEventListener('change', onChange)
    },
    [query],
  )

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false, // Server/first paint: assume the desktop layout.
  )
}

/** Tailwind's breakpoints, so JS and CSS can never disagree about where they are. */
export const BREAKPOINT_PX = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const

export type Breakpoint = keyof typeof BREAKPOINT_PX

/** True once the viewport is at least `breakpoint` wide — the `sm:`/`md:` rules' condition. */
export function useBreakpointUp(breakpoint: Breakpoint): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINT_PX[breakpoint]}px)`)
}
